import { Products, Plugins } from '@one/types'
import { Cart, Shared } from '@one/types/dist/orderpath/app'
import ProductInCart = Shared.ProductInCart
import { AddToCartContext } from '~/plugins/sdk/AddToCartContext'
import Type = Plugins.Type

export const ERRORS = {
  NO_CART_AVAILABLE: 'NO_CART_AVAILABLE',
  PRODUCT_NOT_EXISTS: 'PRODUCT_NOT_EXISTS',
  INVALID_AMOUNT: 'INVALID_AMOUNT',
  NO_PRICE_AVAILABLE: 'NO_PRICE_AVAILABLE',
  PRODUCT_BLOCKED: 'PRODUCT_BLOCKED',
  INVALID_ID: 'INVALID_ID',
  BLOCK_PRODUCT_ABOVE_STOCK: 'BLOCK_PRODUCT_ABOVE_STOCK',
}

type ValidationSuccess = boolean
type ValidationError = [boolean, string]
type ValidationResult =
  | ValidationSuccess
  | ValidationError
  | Promise<ValidationSuccess | ValidationError>

export default function useAddToCart(vm) {
  const DEFAULT_NOTIF_DURATION: number = 6000
  const validationErrorsOptions = {
    [ERRORS.BLOCK_PRODUCT_ABOVE_STOCK](product: Products.Product) {
      const quantityInWarehouses = vm.$store.getters['stocks/sumProductQuantityInWarehouses'](product.id)
      return {
        ignoreDuplicates: true,
        group: 'addToCartAlert',
        type: 'error',
        duration: DEFAULT_NOTIF_DURATION,
        title: `${vm.$t('product_block.title_one')} ${product.name} ${vm.$t(
          'product_block.title_two',
        )}`,
        text: `${vm.$t('product_block.text')} ${quantityInWarehouses}`,
      }
    },
    [ERRORS.PRODUCT_BLOCKED](product: Products.Product) {
      return {
        ignoreDuplicates: true,
        group: 'addToCartAlert',
        type: 'error',
        duration: DEFAULT_NOTIF_DURATION,
        title: `${vm.$t('product_block.product')} ${product.name} ${vm.$t(
          'product_block.is_blocked',
        )}`,
        text: `${vm.$t('product_block.product_blocked_one')} ${product.id} ${vm.$t(
          'product_block.product_blocked_two',
        )} ${product.labels}`,
      }
    },
    [ERRORS.NO_PRICE_AVAILABLE](product: Products.Product) {
      return {
        ignoreDuplicates: true,
        group: 'addToCartAlert',
        type: 'error',
        duration: DEFAULT_NOTIF_DURATION,
        title: `${vm.$t('product_block.cannot_add')} ${product.name}`,
        text: vm.$t('product_block.no_price_available') as string,
      }
    },
    [ERRORS.INVALID_AMOUNT](product: Products.Product) {
      return {
        ignoreDuplicates: true,
        group: 'addToCartAlert',
        type: 'error',
        duration: DEFAULT_NOTIF_DURATION,
        title: `${vm.$t('product_block.cannot_add')} ${product.name}`,
        text: vm.$t('product_block.invalid_amount') as string,
      }
    },
  }

  const defaultErrorOptions = (error: string) => ({
    group: 'alert',
    type: 'error',
    text: error,
  })

  const noAvailableCartError = () => vm.$notify(defaultErrorOptions('No cart is available'))
  const invalidSKUError = () => vm.$notify(defaultErrorOptions('Invalid product SKU'))
  const productDoesNotExistError = () => vm.$notify(defaultErrorOptions('Product does not exists'))
  const customResponseError = (text: string) => vm.$notify(defaultErrorOptions(text))
  const getErrorOptions = (error: string): Function => {
      if (validationErrorsOptions[error]) {
        return validationErrorsOptions[error]
      }
      return () => defaultErrorOptions(error)
    }
  const showError = (error: string, product: Products.Product, amount: number | null) => {
      if (error in ERRORS) {
        vm.$notify(getErrorOptions(error).call(vm, product, amount))
      }
  }
  const showErrors = (errors: Array<string>, product: Products.Product, amount: number | null) => {
    errors.forEach((error: string) => {
      vm.$notify(getErrorOptions(error).call(vm, product, amount))
    })
  }
  const handleResponseError = (e: any, product: Products.Product, amount: number | null) => {
      if (e.response.status === 400) {
        if (e.response.data.type === ERRORS.BLOCK_PRODUCT_ABOVE_STOCK) {
          showError(ERRORS.BLOCK_PRODUCT_ABOVE_STOCK, product, amount)
        } else {
          showError(ERRORS.INVALID_AMOUNT, product, amount)
        }
      } else if (e.response.status === 404) {
        customResponseError(e.response.data.message)
      }
  }
  const runValidators = async (
      product: Products.Product,
      amount: number,
    ): Promise<boolean | Array<string>> => {
      const errors = (
        await Promise.all(
          addToCartValidators.map((validator: any) => validator(product, amount)),
        )
      ).filter((result: ValidationResult) => Array.isArray(result))
      if (errors.length) {
        return errors.map((error: ValidationError) => error[1])
      }
      return true
  }
  const isAmountValid = (product: Products.Product, amount: number): ValidationResult => {
    const units: Shared.ProductMeasurementUnits = product.measurementUnits
    const checkIfIsMemberOfArithmeticProgression = (amountToCheck: number): boolean => {
      const startNumber: number = units.quantityMin || 1
      const progressionNumber: number | null = units.quantityInterval || null
      if (!progressionNumber || progressionNumber === 0) {
        return startNumber === amountToCheck
      }

      return (
        (amountToCheck - startNumber) % progressionNumber === 0 &&
        (amountToCheck - startNumber) / progressionNumber >= 0
      )
    }
    const checkIfAmountIsHigherOrEqualThanMinQuantity = (amountToCheck: number): boolean => {
      if (!units.quantityMin || !Number.isInteger(units.quantityMin)) {
        return true
      }
      return amountToCheck >= units.quantityMin
    }
    const basicAmountValidator = (modifiedAmount: number): ValidationResult => {
      const isPositiveNumber: boolean = modifiedAmount > 0
      const isInProperInterval: boolean = checkIfIsMemberOfArithmeticProgression(modifiedAmount)

      const isHigherThanMinQuantity: boolean =
        checkIfAmountIsHigherOrEqualThanMinQuantity(modifiedAmount)
      return (
        (isPositiveNumber && isInProperInterval && isHigherThanMinQuantity) || [
          false,
          ERRORS.INVALID_AMOUNT,
        ]
      )
    }

    const hasMinQtyAndInterval = (): ValidationResult =>
      !!(units.quantityMin && units.quantityInterval)

    const productInCart: ProductInCart | null = vm.$store.getters['cart/getCartProduct'](vm.$store.getters['cart/getCurrentCartId'])(
      product.id,
    )
    if (!hasMinQtyAndInterval()) {
      return true
    }
    if (productInCart !== null) {
      const cartQuantity: number = vm.$store.getters['cart/getTotalProductCountInCart'](
        product.id,
        vm.$store.getters['cart/getCurrentCartId'],
      )
      const modifiedProductQuantity: number = cartQuantity + amount
      return basicAmountValidator(modifiedProductQuantity)
    }
    return basicAmountValidator(amount)
  }

  const isPriceAvailable = async (product: Products.Product): Promise<ValidationResult> => {
      let price: Products.ProductPrice = vm.$store.getters['products/productPrice'](product.id)
    if (!price) {
      price = await vm.$store.dispatch('products/fetchProductPrice', {
        productId: product.id,
        quantity: 1,
      })
    }
    if (!price) {
      return [false, ERRORS.NO_PRICE_AVAILABLE]
    }
    return true
  }

  const getOrFetchProduct = async (productId: string): Promise<Products.Product | null> => {
    try {
      return vm.$store.getters['products/getProduct'](productId) || (await vm.$store.dispatch('products/fetchProductWithPriceAndStock', productId))
    } catch (e) {
      return Promise.resolve(null)
    }
  }

  const pushGtmEvent = (product: Products.Product, where: string, amount: number) => {
    const price = vm.$store.getters['products/productPrice'](product.id)
    const discount = (price?.catalogPriceNet - price?.priceNet) || 0
    vm.$gtm.productAdd(
      {
        item_id: product.id,
        item_name: product.name,
        item_list_name: where,
        item_brand: product.brand?.name || '',
        category: vm.$routes.getCategoryPath(product.canonicalCategory) || '',
        price: price?.priceNet || 0,
        discount: discount,
        quantity: amount,
      },
      price?.priceNet * amount ?? 0,
    )
  }

  const defaultAddToCartFlow = async (product: Products.Product, amount: number, where: string, cartId: string) => {
    try {
      const errors = await runValidators(product, amount)
      if (Array.isArray(errors)) {
        showErrors(errors, product, amount)
        return null
      }
      await vm.$store.dispatch('cart/addProductToCart', {
        cartId: cartId,
        newProduct: {
          productId: product.id,
          amountOfProducts: amount,
        },
        warehouseId: vm.$store.getters['stocks/getDefaultWarehouseId'],
      })
      pushGtmEvent(product, where, amount)
      vm.$notify({
        group: 'alert',
        type: 'success',
        text: vm.$t('product_page.alert_added_to_cart') as string,
      })
      return product
    } catch (e: any) {
      if (e.response?.errorCode === 'orderpath-backoffice-cart-CartNotFoundError') {
        const newCart: string = await vm.$orderpath.cart.createCartAndSelect()
        if (newCart) {
          return addToCart(product.id, amount, where, newCart)
        }
      }
      vm.$logger.warn(e)
      if (e.response) {
        handleResponseError(e, product, amount)
      }
      return null
    }
  }

  const addToCart = async (
      productId: string,
      amount: number,
      where: string,
      cartId?: string,
  ): Promise<Products.Product | null> => {
    const currentCartId: string = cartId || vm.$store.getters['cart/getCurrentCartId']
    if (!currentCartId) {
      noAvailableCartError()
      return null
    }
    if (productId.trim() === '') {
      invalidSKUError()
      return null
    }
    const product: Products.Product | null = await getOrFetchProduct(productId)
    if (!product) {
      productDoesNotExistError()
      return null
    }
    if (Object.keys(product.pluginsData).length < 1) {
      return defaultAddToCartFlow(product, amount, where, currentCartId)
    } else {
      const pluginId = await vm.$store.dispatch('plugins/getPluginIdToLaunch', {
        productPlugins: product.pluginsData,
        type: Type.IFRAME_SHOP_ADD_TO_CART,
      })

      if (!pluginId) {
        return defaultAddToCartFlow(product, amount, where, currentCartId)
      }
      try {
        const cartProducts: Array<ProductInCart> = Array.from(vm.$store.getters['cart/getCartProducts'](currentCartId).values())
        const context = new AddToCartContext(pluginId, vm.$store, {
          meta: product.pluginsData[pluginId],
          pluginProductLines: [],
          productLines: cartProducts,
          warehouseId: vm.$store.getters['stocks/getDefaultWarehouseId'],
          warehouses: vm.$store.getters['stocks/getWarehousesList'],
          cartId: currentCartId,
          productId: product.id,
          productAmount: amount,
        })
        context.onClose(() => {
          vm.$api.orderpath.app.invalidate(['cart'])
          vm.$orderpath.cart.fetchCart(vm.$store.getters['cart/getCurrentCartId'])
          return false
        })
        context.open()
      } catch (e) {
        console.log(e)
        return defaultAddToCartFlow(product, amount, where, currentCartId)
      }
    }
    return null
  }

  const addSelectedToCart = async (
    newProducts: Array<Cart.Requests.AddProductToCart>,
    where: string,
    cartId?: string,
  ): Promise<any> => {
    const currentCartId: string = cartId || vm.$store.getters['cart/getCurrentCartId']
    if (!currentCartId) {
      noAvailableCartError()
      return null
    }
    newProducts.map(async (elem: Cart.Requests.AddProductToCart) => {
      if (elem.productId.trim() === '') {
        invalidSKUError()
        return null
      }
      const product: Products.Product | null = await getOrFetchProduct(elem.productId)
      if (!product) {
        /*
            FIXME: W ramach UNITYONE-7119 ukrywam ten popup do momentu,
                   aż catalog nie udostępni nowego endpointa
         */
        // this.productDoesNotExistError()
        return null
      }
      const errors = await runValidators(product, elem.amountOfProducts)
      if (Array.isArray(errors)) {
        showErrors(errors, product, elem.amountOfProducts)
        return null
      }
    })
    try {
      await vm.$store.dispatch('cart/addSelectedProductsToCart', {
        cartId: currentCartId,
        newProducts: newProducts,
        warehouseId: vm.$store.getters['stocks/getDefaultWarehouseId'],
      })
      newProducts.map(async (newProduct: Cart.Requests.AddProductToCart) => {
        const price: Products.ProductPrice = vm.$store.getters['products/productPrice'](newProduct.productId)
        const product: Products.Product | null = await getOrFetchProduct(newProduct.productId)
        const discount = price.catalogPriceNet || 0 - price.priceNet || 0

        if (product) {
          const { name, id, manufacturer, canonicalCategory, brand } = product
          vm.$gtm.productAdd(
            {
              item_name: name,
              item_id: id,
              item_list_name: where,
              item_brand: (brand && brand.name) || manufacturer.name,
              category: vm.$routes.getCategoryPath(canonicalCategory) || '',
              quantity: newProduct.amountOfProducts,
              price: price.priceNet,
              discount: discount,
            },
            price.priceNet * newProduct.amountOfProducts,
          )
        }
      })
      vm.$notify({
        group: 'alert',
        type: 'success',
        text: vm.$t('product_page.alert_added_to_cart') as string,
      })
      return Promise.resolve(true)
    } catch (e: any) {
      if (e.response) {
        e.response.data.map(async (newProduct: Cart.Requests.AddProductToCart) => {
          const product: Products.Product | null = await getOrFetchProduct(
            newProduct.productId,
          )
          if (!product) {
            productDoesNotExistError()
            return null
          }
          handleResponseError(e, product, null)
        })
      }
      return Promise.reject(e)
    }
  }

  const addToCartValidators = [isAmountValid, isPriceAvailable]

  return { getOrFetchProduct, addToCart, addSelectedToCart }
}
